import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPut, unwrap } from '@/shared/api'
import { toast } from '@/shared/ui/toast'
import type { Notification, NotificationKind, NotificationSubscription } from '../model/schema'
import {
  type NotificationDto,
  type NotificationSubscriptionDto,
  toNotification,
  toNotificationSubscriptions,
} from './mappers'

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (limit: number, offset: number) =>
    [...notificationKeys.lists(), { limit, offset }] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  subscriptions: () => [...notificationKeys.all, 'subscriptions'] as const,
}

// Лента уведомлений (created_at DESC). Растущий limit при offset=0 — единственный
// list-кэш, поэтому оптимистика read/mark-all патчит одну запись (см. мутации ниже).
export function notificationsQueryOptions(limit: number, offset = 0) {
  return queryOptions({
    queryKey: notificationKeys.list(limit, offset),
    queryFn: async (): Promise<Notification[]> => {
      const list =
        unwrap<NotificationDto[]>(
          await apiGet('/notifications', { limit: String(limit), offset: String(offset) }),
        ) ?? []
      return list.map(toNotification)
    },
  })
}

// Бейдж непрочитанных. Ответ — { count } мимо конверта { data }, читаем точечно.
// Поллинг 30с (docs/design §4: живость без websocket — только счётчики).
export const unreadCountQueryOptions = queryOptions({
  queryKey: notificationKeys.unreadCount(),
  queryFn: async (): Promise<number> => {
    const body = (await apiGet('/notifications/unread-count')) as { count: number }
    return body.count
  },
  refetchInterval: 30_000,
})

function snapshot(qc: ReturnType<typeof useQueryClient>) {
  return {
    lists: qc.getQueriesData<Notification[]>({ queryKey: notificationKeys.lists() }),
    count: qc.getQueryData<number>(notificationKeys.unreadCount()),
  }
}

function restore(qc: ReturnType<typeof useQueryClient>, snap: ReturnType<typeof snapshot>) {
  for (const [key, data] of snap.lists) qc.setQueryData(key, data)
  qc.setQueryData(notificationKeys.unreadCount(), snap.count)
}

// Гасим ● в каждом list-кэше и уменьшаем бейдж только на реально непрочитанные из ids.
function patchRead(qc: ReturnType<typeof useQueryClient>, ids: Set<string>, prevCount?: number) {
  const nowIso = new Date().toISOString()
  let freshlyRead = 0
  qc.setQueriesData<Notification[]>({ queryKey: notificationKeys.lists() }, (list) =>
    list?.map((n) => {
      if (!ids.has(n.id) || n.readAt) return n
      freshlyRead += 1
      return { ...n, readAt: nowIso }
    }),
  )
  if (prevCount !== undefined) {
    qc.setQueryData(notificationKeys.unreadCount(), Math.max(0, prevCount - freshlyRead))
  }
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: notificationKeys.lists() })
  void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
}

// Пометить прочитанным (PUT /notifications/{id}/read → 204). Оптимистично: клик по
// строке гасит ● и уменьшает бейдж; откат при ошибке.
export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiPut(`/notifications/${id}/read`),
    onMutate: async (id): Promise<ReturnType<typeof snapshot>> => {
      await qc.cancelQueries({ queryKey: notificationKeys.all })
      const snap = snapshot(qc)
      patchRead(qc, new Set([id]), snap.count)
      return snap
    },
    onError: (_err, _id, snap) => {
      if (snap) restore(qc, snap)
      toast.error('Не удалось отметить прочитанным')
    },
    onSettled: () => invalidate(qc),
  })
}

// «Прочитать всё» — клиентский цикл PUT read по видимым непрочитанным, одна
// инвалидация в конце (docs/design/screens/inbox.md: массовая отметка).
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) =>
      Promise.all(ids.map((id) => apiPut(`/notifications/${id}/read`))),
    onMutate: async (ids): Promise<ReturnType<typeof snapshot>> => {
      await qc.cancelQueries({ queryKey: notificationKeys.all })
      const snap = snapshot(qc)
      patchRead(qc, new Set(ids), snap.count)
      return snap
    },
    onError: (_err, _ids, snap) => {
      if (snap) restore(qc, snap)
      toast.error('Не удалось отметить всё прочитанным')
    },
    onSettled: () => invalidate(qc),
  })
}

// Подписки на виды алертов в телеграме. Порядок каталожный и его задаёт бэк —
// мапим то, что пришло, чтобы строки не переставлялись между запросами.
export const notificationSubscriptionsQueryOptions = queryOptions({
  queryKey: notificationKeys.subscriptions(),
  queryFn: async (): Promise<NotificationSubscription[]> =>
    toNotificationSubscriptions(
      unwrap<NotificationSubscriptionDto[]>(await apiGet('/notifications/subscriptions')) ?? [],
    ),
})

// Тумблер вида: PUT идемпотентен (шлём состояние, а не дельту). Оптимистично —
// коробка отвечает сразу, ошибка возвращает состояние и поднимает тост.
export function useSetNotificationSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ kind, enabled }: { kind: NotificationKind; enabled: boolean }) =>
      apiPut(`/notifications/subscriptions/${kind}`, { enabled }),
    onMutate: async ({ kind, enabled }): Promise<NotificationSubscription[] | undefined> => {
      await qc.cancelQueries({ queryKey: notificationKeys.subscriptions() })
      const prev = qc.getQueryData<NotificationSubscription[]>(notificationKeys.subscriptions())
      qc.setQueryData<NotificationSubscription[]>(notificationKeys.subscriptions(), (list) =>
        list?.map((s) => (s.kind === kind ? { ...s, enabled } : s)),
      )
      return prev
    },
    onError: (_err, _vars, prev) => {
      if (prev) qc.setQueryData(notificationKeys.subscriptions(), prev)
      toast.error('Не удалось сохранить настройку')
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.subscriptions() })
    },
  })
}
